import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { UrlQuery } from '@/features/url-handling'
import { createSelectors } from '@/utils/createSelectors'

import { AppConfigProps } from './types'

type AppConfigState = AppConfigProps & {
	rdy: boolean
	urlQuery: UrlQuery | undefined
	setAppConfigState: (props: AppConfigProps) => void
	setUrlQuery: (data: UrlQuery) => void
}

const useAppConfigStoreBase = create<AppConfigState, [['zustand/devtools', never]]>(
	devtools(
		(set) => ({
			locationFilterDisabled: false,
			rootUri: '',
			theme: '',
			inBackend: false,
			enableServices: false,
			heroTitle: '',
			image: {
				src: '',
				srcset: '',
				sizes: '',
				alt: '',
				width: null,
				height: null,
				title: '',
			},
			optirezCheckoutUrl: '',
			footerLinks: {
				languages: [],
				navigation: [],
			},
			reservationMail: '',
			translations: {
				'step-01-title': 'Urlaubsort',
				'step-01-instructions': 'Bitte wählen Sie Ihren Urlaubsort',
				'step-01-progess-title': 'Urlaubsort',
				'step-02-title': 'Zimmerbelegung',
				'step-02-instructions': 'Bitte konfigurieren Sie Ihre Zimmer',
				'step-02-progess-title': 'Zimmerbelegung',
				'step-03-title': 'Reisezeitraum',
				'step-03-instructions': 'Bitte wählen Sie Ihren Reisezeitraum',
				'step-03-progess-title': 'Reisezeitraum',
				'step-04-title': 'Zimmerauswahl',
				'step-04-instructions': 'Wählen Sie Ihre gewünschten Zimmer',
				'step-04-progess-title': 'Zimmerauswahl',
				'step-05-title': 'Meine Daten',
				'step-05-instructions': 'Bitte geben Sie Ihre Daten ein',
				'step-05-progess-title': 'Meine Daten',
				'step-06-title': 'Zahlung',
				'step-06-instructions': 'Bitte geben Sie Ihre Zahlungsdaten ein',
				'step-06-progess-title': 'Zahlung',
				all: ' Alle',
				'progress-total-price': 'Gesamtpreis',
				'promotion-title': 'Promotion',
				'promotion-instruction': 'Haben Sie einen Promocode? ',
				'promotion-button-label': 'Prüfen',
				'promotion-placeholder': 'Bitte Promocode eingeben',
				'promotion-error': 'Bitte geben Sie Ihren Code nach folgendem Muster ein XYZ',
				'promotion-more-info': 'Mehr Infos zu den Promocode- Konditionen',
				'promotion-modal-title': 'Informationen zu Ihrem Promocode',
				'promotion-valid-for': 'Gültig für',
				'promotion-constraints': 'Bedingungen',
				'promotion-description': 'Beschreibung',
				'location-types-filter-label': 'Wählen Sie die Art der Unterkunft',
				'location-destination-filter-label': 'Wählen Sie Ihr Reiseziel',
				'location-view-count':
					'Mehr als {0} Personen interessieren sich ebenfalls für dieses Angebot',
				'room-config-add-room': 'Raum hinzufügen',
				'room-config-submit-configuration': 'Weiter',
				'room-config-room': 'Zimmer',
				'room-config-rooms': 'Zimmer',
				'room-config-amount-of-persons': 'Anzahl der Personen',
				'room-config-adults': 'Erwachsene',
				'room-config-child': 'Kind',
				'room-config-children': 'Kinder',
				'room-config-children-age-departure': 'Alter der Kinder bei Abreise',
				'travel-period-summary-title': 'Ihr Reisezeitraum',
				'travel-period-arrival': 'Anreise',
				'travel-period-departure': 'Abreise',
				'travel-period-night': '{0} Nacht',
				'travel-period-nights': '{0} Nächte',
				'travel-period-holiday-placeholder': 'Feiertage & Ferien in...',
				'travel-period-your-selected-travelperiod': 'Ihr Reisezeitraum',
				'travel-period-pricerange-description':
					'Finden Sie den besten Preis auf einen Blick',
				'travel-period-pricerange-no-arrival': 'Kein Anreisetag',
				'travel-period-pricerange-public-holiday': 'Feiertag',
				'travel-period-pricerange-school-holiday': 'Ferien',
				'travel-period-pricerange-applied-promotion': 'Für diesen Tag gilt Ihr Promocode',
				'travel-period-submit-travelperiod': 'Weiter',
				'travel-period-from-price': 'ab',
				'travel-period-months-january': 'Januar',
				'travel-period-months-february': 'Februar',
				'travel-period-months-march': 'März',
				'travel-period-months-april': 'April',
				'travel-period-months-may': 'Mai',
				'travel-period-months-june': 'Juni',
				'travel-period-months-july': 'Juli',
				'travel-period-months-august': 'August',
				'travel-period-months-september': 'September',
				'travel-period-months-october': 'Oktober',
				'travel-period-months-november': 'November',
				'travel-period-months-december': 'Dezember',
				'travel-period-days-short-monday': 'Mo',
				'travel-period-days-short-tuesday': 'Di',
				'travel-period-days-short-wednesday': 'Mi',
				'travel-period-days-short-thursday': 'Do',
				'travel-period-days-short-friday': 'Fr',
				'travel-period-days-short-saturday': 'Sa',
				'travel-period-days-short-sunday': 'So',
				'travel-period-min-stay-head': 'Mindestaufenthalt',
				'travel-period-min-stay-text':
					'Ihr gewählter Ankunftstag ist mit einem Mindestaufenthalt von {0} Nächten verbunden. Die früheste Abreise wäre am {1} möglich.',
				'travel-period-adjust-period': 'Zeitraum anpassen',
				'travel-period-adjust-cancel': 'Zurücksetzen',
				'cross-selling-title': 'Ausgebucht?',
				'cross-selling-description':
					'Probieren Sie es in einem unserer anderen Häuser ganz in der Nähe',
				'cross-selling-no-hits':
					'Leider können wir Ihnen zu Ihrer Anfrage keine Angebote ausspielen. Bitte versuchen Sie es mit anderen Daten.',
				'cross-selling-link': 'Zum Angebot',
				'room-selection-back': 'Zurück',
				'room-selection-room': 'Zimmer',
				'room-selection-submit': 'Weiter',
				'room-selection-cancel-till': 'Kostenlos stornieren bis',
				'room-selection-additions': 'Extras',
				'room-selection-sum': 'Summe',
				'room-selection-edit-room': ' Zimmer bearbeiten',
				'room-selection-details': 'Details',
				'room-selection-room-booking': 'Zimmer Buchen',
				'room-selection-available-rooms': 'Nur noch {0} Zimmer verfügbar',
				'room-selection-adults': 'Erwachsene',
				'room-selection-children': 'Kinder',
				'room-selection-occupancy-mismatch':
					'Zimmer nicht verfügbar - Personenanzahl überschritten',
				'room-selection-not-available': 'Zimmer ausgebucht',
				'room-selection-recommended': 'Tipp',
				'guest-data-mandatory': 'Pflichtfeld',
				'guest-data-salutation-mr': 'Herr',
				'guest-data-pls-select': 'Bitte wählen',
				'guest-data-salutation-mrs': 'Frau',
				'guest-data-salutation-mr.dr': 'Herr Dr.',
				'guest-data-salutation-mrs.dr': 'Frau Dr.',
				'guest-data-salutation-mr.prof.dr': 'Herr Prof. Dr.',
				'guest-data-salutation-mrs.prof.dr': 'Frau Prof. Dr.',
				'guest-data-salutation-mr.prof': 'Herr Prof.',
				'guest-data-salutation-msr.prof': 'Frau Prof.',
				'guest-data-salutation': 'Anrede',
				'guest-data-title': 'Title',
				'guest-data-name': 'Vorname',
				'guest-data-surname': 'Nachname',
				'guest-data-contact': 'Kontakt',
				'guest-data-email': 'E-Mail',
				'guest-data-phoneNumber': 'Telefon',
				'guest-data-address': 'Anschrift',
				'guest-data-country': 'Land',
				'guest-data-postCode': 'Postleitzahl',
				'guest-data-city': 'Ort',
				'guest-data-street': 'Straße',
				'guest-data-streetNumber': 'Hausnummer',
				'guest-data-booking-for': 'Für wen buchen Sie?',
				'guest-data-bookingFor-me': 'Buchung ist für mich',
				'guest-data-bookingFor-other': 'Buchung ist für hemand anderes',
				"guest-data-room'": 'Reisende Zimmer {0}',
				'guest-data-roomPersons': 'Reisende Person {0}',
				'guest-data-error-salutation': ' Bitte geben Sie eine Anrede an',
				'guest-data-error-name': ' Bitte geben Sie einen Vornamen an',
				'guest-data-error-surname': ' Bitte geben Sie einen Nachnamen an',
				'guest-data-error-email': ' Bitte geben Sie eine E-Mail an',
				'guest-data-error-phoneNumber': ' Bitte geben Sie eine Telefonnummer an',
				'guest-data-error-country': ' Bitte geben Sie ein Land an',
				'guest-data-error-postCode': ' Bitte geben Sie eine Postleitzahl an',
				'guest-data-error-city': ' Bitte geben Sie ein Ort an',
				'guest-data-error-street': ' Bitte geben Sie eine Straße an',
				'guest-data-error-streetNumber': ' Bitte geben Sie eine Hausnummer an',
				'guest-data-booking-payment-method': 'Zahlungsart',
				'guest-data-booking-payment-legal':
					'Die Europäische Union hat eine neue Richtlinie für Zahlungsdienste beschlossen – den Payment Service Directive 2 (PSD2). Diese Richtlinie soll das Bezahlen im Internet sicherer machen. Wenn Sie eine neue Kreditkarte mit PSD2 besitzen',
				'guest-data-payment-method-creditCard-arrive': 'Kreditkarte (Zahlung bei Abreise)',
				'guest-data-payment-method-creditCard-booking':
					'Kreditkarte (Zahlung nach Buchung)',
				'guest-data-payment-method-paypal': 'PayPal (Zahlung nach Buchung)',
				'guest-data-payment-method-applepay': 'ApplePay (Zahlung bei Buchung)',
				'guest-data-payment-method-klarna': 'Klarna (Zahlung bei Buchung)',
				'guest-data-annotation': 'Anmerkungen',
				'guest-data-annotation-placeholder': 'Tippen Sie hier Fragen',
				'guest-data-late-arrive': 'Spätanreise',
				'footer-label-corporate': 'Unternehmen',
				'footer-label-gdpr': 'Datenschutz & Sicherheit',
				'footer-label-legal': 'Rechtliches',
				'footer-label-navigation': 'Navigation',
				'footer-label-cookieSettings': 'Cookie Einstellungen',
				'footer-navigation-get-support-code': 'Support Code anfordern',
				'footer-navigation-support-code': 'Ihr Support-Code',
				'footer-navigation-support-code-description':
					'Bitte nennen Sie diesen Code, wenn sie die Hotline anrufen.',
				'footer-navigation-help': 'Hilfe',
				'footer-navigation-language': 'Sprache',
			},
			language: 'de',
			initialSearchQuery: {},
			successPageUrl: '',
			disableBookingHotlineDisplay: false,
			rdy: false,
			urlQuery: undefined,
			legalTextUrls: {
				hotelConditionUrl: '',
				parkingConditionUrl: '',
				privacyPolicyUrl: '',
			},
			newsLetterIdentifier: '',
			newsLetterListName: '',
			newsLetterTextName: '',
			setAppConfigState: (state: AppConfigProps) =>
				set(() => ({ ...state, rdy: true }), false, 'setAppConfigState'),
			setUrlQuery: (data: UrlQuery) =>
				set((state) => ({ ...state, urlQuery: data }), false, 'setUrlQuery'),
		}),
		{ name: 'name', store: 'AppConfigStore' }
	)
)

export const useAppConfigStore = createSelectors(useAppConfigStoreBase)
